import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const NAV = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/clients', label: 'Clients' },
  { to: '/creative-management', label: 'Creative Management' },
  { to: '/green-room', label: 'The Green Room' },
  { to: '/news', label: 'News' },
  { to: '/contact', label: 'Contact' },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener('scroll', on, { passive: true });
    return () => window.removeEventListener('scroll', on);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-[#002b5c]/85 backdrop-blur-md py-3 border-b border-white/10'
            : 'bg-[#002b5c] py-5'
        }`}
      >
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" aria-label="David Ball Management, home" className="flex items-center group shrink-0 mr-6">
            <span style={{ letterSpacing: "0.1em" }} className="font-heading font-semibold text-white text-lg sm:text-2xl 2xl:text-[28px] uppercase leading-none whitespace-nowrap">
              David Ball <span className="text-[#D4B06A] transition-colors duration-300 group-hover:text-white">Management</span>
            </span>
          </Link>

          <nav className="hidden xl:flex items-center gap-6 2xl:gap-9">
            {NAV.slice(1).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `relative font-body text-[12px] tracking-[0.12em] 2xl:text-[13px] 2xl:tracking-[0.15em] uppercase whitespace-nowrap transition-colors ${
                    isActive ? 'text-[#C5A059]' : 'text-white/85 hover:text-white'
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="nav-apply-btn"
              className="hidden md:inline-flex items-center whitespace-nowrap rounded-full border border-[#C5A059] text-white px-5 py-2 text-[11px] tracking-[0.2em] uppercase hover:bg-[#C5A059] hover:text-[#001a38] transition-all duration-500"
            >
              Apply for Representation
            </Link>
            <button
              aria-label="Menu"
              data-testid="nav-menu-toggle"
              onClick={() => setOpen((o) => !o)}
              className="xl:hidden text-white p-2"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        data-testid="nav-mobile-drawer"
        className={`fixed inset-0 z-40 bg-[#001a38] transition-opacity duration-500 xl:hidden ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="pt-28 px-8 flex flex-col gap-6">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
              className="font-heading text-white text-4xl uppercase tracking-tight hover:text-[#C5A059] transition-colors"
            >
              {n.label}
            </Link>
          ))}
          <div className="mt-6 hairline" />
          <Link
            to="/contact"
            className="mt-4 inline-flex w-fit items-center rounded-full bg-[#C5A059] text-[#001a38] px-6 py-3 text-xs tracking-[0.2em] uppercase"
          >
            Apply for Representation
          </Link>
        </div>
      </div>
    </>
  );
}
